import { encode } from "blurhash";

/**
 * Generate a blurhash from an image file
 */
export async function generateBlurhash(
  file: File,
  componentX: number = 4,
  componentY: number = 4,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // resize image for blurhash generation (smaller = faster)
      const maxSize = 64;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      try {
        const imageData = ctx.getImageData(0, 0, width, height);
        const blurhash = encode(
          imageData.data,
          imageData.width,
          imageData.height,
          componentX,
          componentY,
        );
        resolve(blurhash);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate a blurhash from an image URL
 */
export async function generateBlurhashFromUrl(
  imageUrl: string,
  componentX: number = 4,
  componentY: number = 4,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    // first attempt: try with CORS
    img.crossOrigin = "anonymous";

    img.onload = () => {
      // resize image for blurhash generation
      const maxSize = 64;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      try {
        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);
        const blurhash = encode(
          imageData.data,
          imageData.width,
          imageData.height,
          componentX,
          componentY,
        );
        resolve(blurhash);
      } catch (error) {
        // canvas operations failed due to CORS
        reject(new Error("Canvas operations blocked by CORS policy"));
      }
    };

    img.onerror = (event) => {
      // if CORS-enabled request failed, try without CORS
      if (img.crossOrigin === "anonymous") {
        console.warn("CORS request failed, trying without CORS...");
        const fallbackImg = new Image();

        fallbackImg.onload = () => {
          // try the same processing without CORS
          const maxSize = 64;
          let { width, height } = fallbackImg;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
          }

          try {
            ctx.drawImage(fallbackImg, 0, 0, width, height);
            const imageData = ctx.getImageData(0, 0, width, height);
            const blurhash = encode(
              imageData.data,
              imageData.width,
              imageData.height,
              componentX,
              componentY,
            );
            resolve(blurhash);
          } catch (error) {
            reject(
              new Error(
                "Canvas operations blocked - image likely from different origin",
              ),
            );
          }
        };

        fallbackImg.onerror = () => {
          reject(
            new Error(
              "Failed to load image from URL (both CORS and non-CORS attempts failed)",
            ),
          );
        };

        // don't set crossOrigin for fallback attempt
        fallbackImg.src = imageUrl;
      } else {
        reject(new Error("Failed to load image from URL"));
      }
    };

    img.src = imageUrl;
  });
}

/**
 * Validate if a string is a valid blurhash
 */
export function isValidBlurhash(blurhash: string): boolean {
  if (!blurhash || typeof blurhash !== "string") {
    return false;
  }

  // basic blurhash validation
  try {
    const regex = /^[\w#$%*+,\-.:;=?@[\]^_{|}~]+$/;
    return regex.test(blurhash) && blurhash.length >= 6;
  } catch {
    return false;
  }
}

/**
 * Get optimal blurhash components based on image aspect ratio
 */
export function getOptimalComponents(
  width: number,
  height: number,
): {
  componentX: number;
  componentY: number;
} {
  const aspectRatio = width / height;

  if (aspectRatio > 1.5) {
    // wide image
    return { componentX: 6, componentY: 4 };
  } else if (aspectRatio < 0.75) {
    // tall image
    return { componentX: 4, componentY: 6 };
  } else {
    // square-ish image
    return { componentX: 4, componentY: 4 };
  }
}

/**
 * Process multiple images and generate blurhashes
 */
export async function processImagesWithBlurhash(files: File[]): Promise<
  Array<{
    file: File;
    blurhash: string;
    error?: string;
  }>
> {
  const results = await Promise.allSettled(
    files.map(async (file) => {
      try {
        const blurhash = await generateBlurhash(file);
        return { file, blurhash };
      } catch (error) {
        return {
          file,
          blurhash: "",
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    }),
  );

  return results.map((result) => {
    if (result.status === "fulfilled") {
      return result.value;
    } else {
      return {
        file: new File([], "unknown"),
        blurhash: "",
        error: "Processing failed",
      };
    }
  });
}
